import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef, useRef, createElement } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import mergeRefs from 'react-merge-refs';
import Icon from '../Icon/Icon.js';
import { CommonClasses } from '../../commonClasses.js';

const INPUT_PROPS = {
  size: ['large', 'medium', 'small']
};
const BASE_CLASS = 'nui-input';
const Classes = {
  BASE: BASE_CLASS,
  WRAPPER: `${BASE_CLASS}-wrapper`,
  BUTTON: `${BASE_CLASS}-button`,
  DISABLED: `${BASE_CLASS}--disabled`,
  LEFT_PADDING: `${BASE_CLASS}--left-padding`,
  RIGHT_PADDING: `${BASE_CLASS}--right-padding`
};
const inputPropTypes = {
  className: PropTypes.string,
  containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
  /** disable the input and make it semi-transparent */
  disabled: PropTypes.bool,
  /** element that will be rendered before the input. Must be styled separately */
  leftElement: PropTypes.node,
  /** left icon name. Shorthand for leftIconProps={{ name, title: name }} */
  leftIcon: PropTypes.string,
  /** props for Icon that will be rendered at left side */
  leftIconProps: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string
  }),
  /** element that will be rendered after the input. Must be styled separately */
  rightElement: PropTypes.node,
  /** right icon name. Shorthand for rightIconProps={{ name, title: name }} */
  rightIcon: PropTypes.string,
  /** props for Icon that will be rendered at right side */
  rightIconProps: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string
  }),
  /** Whether input should appear with round sides, default for type=search */
  round: PropTypes.bool,
  /** input size */
  size: PropTypes.oneOf(INPUT_PROPS.size),
  /** type of `input` element or `textarea` */
  type: PropTypes.string
};
/**
 * Wrapper around `input` or `textarea` element.
 * Allows to render icon or any other element before or after the input.
 */
const Input = /*#__PURE__*/forwardRef(({
  className,
  containerRef,
  disabled = false,
  leftElement,
  leftIcon,
  leftIconProps,
  rightElement,
  rightIcon,
  rightIconProps,
  round = false,
  size = 'medium',
  type = 'text',
  ...restProps
}, ref) => {
  const inputRef = useRef(null);
  const handleIconClick = () => inputRef.current?.focus();
  const maybeRenderIcon = (element, iconName, iconProps) => {
    if (element) return element;
    if (iconProps?.name) return /*#__PURE__*/React__default.createElement(Icon, _extends({
      fill: "primary",
      onClick: handleIconClick
    }, iconProps));
    if (iconName) return /*#__PURE__*/React__default.createElement(Icon, {
      fill: "primary",
      onClick: handleIconClick,
      name: iconName,
      title: iconName
    });
    return null;
  };
  const renderElement = () => {
    const inputProps = {
      disabled,
      type,
      'aria-disabled': disabled,
      autoComplete: 'off',
      className: cx(Classes.BASE, {
        [CommonClasses.ROUND]: round,
        [Classes.LEFT_PADDING]: !!(leftElement || leftIcon || leftIconProps?.name),
        [Classes.RIGHT_PADDING]: !!(rightElement || rightIcon || rightIconProps?.name)
      }),
      ...restProps
    };
    return /*#__PURE__*/createElement(type === 'textarea' ? 'textarea' : 'input', {
      ...inputProps,
      ref: mergeRefs([ref, inputRef])
    });
  };
  return /*#__PURE__*/React__default.createElement("div", {
    className: cx(Classes.WRAPPER, `${BASE_CLASS}--${type}`, type !== 'textarea' && `${BASE_CLASS}--${size}`, disabled && Classes.DISABLED, className),
    ref: containerRef
  }, maybeRenderIcon(leftElement, leftIcon, leftIconProps), renderElement(), maybeRenderIcon(rightElement, rightIcon, rightIconProps));
});
Input.propTypes = inputPropTypes;
Input.displayName = 'NuiInput';
var Input$1 = Input;

export { Classes, INPUT_PROPS, Input$1 as default };
